/*
 * ====================================================================
 * PAY ATTENTION!
 * ====================================================================
 * Timeseries config have a strong tie to definitions.go
 * (go/src/blokable.com/api/definitions.go)
 * Make sure to update both files to get it working properly
 * ====================================================================
*/

import moment from 'moment'
import 'moment-duration-format'
import deepmerge from 'deepmerge'
import {
  mdiAirFilter,
  mdiBattery50,
  mdiBell,
  mdiChip,
  mdiCupWater,
  mdiDoorOpen,
  mdiFlash,
  mdiKey,
  mdiPeriodicTableCo2,
  mdiPipeLeak,
  mdiPower,
  mdiPowerPlug,
  mdiShowerHead,
  mdiSmokeDetector,
  mdiSnowflake,
  mdiSolarPower,
  mdiThermometer,
  mdiThermostat,
  mdiThermostatBox,
  mdiWater,
  mdiWaterPercent,
  mdiWaterOff,
  mdiWhiteBalanceSunny,
  mdiWifi,
  mdiWindowClosed,
} from '@mdi/js'

function celsiusToFahrenheit(v) {
  return (v * 1.8) + 32
}

function formatSimple(v) {
  return v.toFixed(1)
}

// eslint-disable-next-line no-unused-vars
function formatButton(v) {
  return 'Pressed'
}

function formatAlarm(v) {
  return v > 0 ? 'ALARM' : 'OK'
}

function statusAlarm(v) {
  return v > 0 ? 'error' : 'ok'
}

function statusDeviceStatus(v) {
  return (v && v.toString() === 'Online') ? 'ok' : 'warning'
}

function formatTemperature(v, convert = this.convert) {
  const f = convert(v)
  return `${f.toFixed(1)} \u00b0F`
}

function formatHumidity(v) {
  return `${v.toFixed(1)} %`
}

function formatUptime(v) {
  return moment.duration(v, 'seconds').format('d [days], h [hr], m [min]')
}

function formatPower(v) {
  return `${v.toFixed(0)} W`
}

function formatVoltage(v) {
  return `${v.toFixed(1)} V`
}

function formatCurrent(v) {
  return `${v.toFixed(1)} A`
}

function formatBattery(v) {
  return `${v.toFixed(0)} %`
}

function formatParticleDensity(v) {
  return `${v.toFixed(1)} \u00B5g/m\u00B3`
}

function formatAQI(v) {
  return `${v.toFixed(0)}`
}

function formatVOC(v) {
  return `${v.toFixed(1)}`
}

function formatPPM(v) {
  return `${v.toFixed(0)} ppm`
}

function formatString(v) {
  return v && v.toString()
}

function formatDoorAccessLog(v) {
  const s = []
  if (v.name !== '') {
    s.push(v.name)
  } else {
    s.push('Unknown')
  }
  if (v.success) {
    s.push('was granted')
  } else {
    s.push('was denied')
  }
  s.push('access')
  s.push('-')
  if (v.credential.card) {
    s.push(`Card: ${v.credential.card}`)
  }
  if (v.credential.pin) {
    s.push(`PIN: ${v.credential.pin}`)
  }
  return s.join(' ')
}

function formatWaterUsage(v) {
  const gpm = v * 0.264172 // Liters/min -> Gal/min
  return `${gpm.toFixed(1)} gpm`
}

function formatOpenClosed(v) {
  return v > 0 ? 'Closed' : 'Open'
}

function formatClosedOpen(v) {
  return formatOpenClosed(1 - v)
}

// Common defaults
const INDOOR_HUMIDITY_DEFAULTS = {
  icon: mdiWaterPercent,
  format: formatHumidity,
  chart: {
    min: 0,
    max: 100,
    lower: 30,
    upper: 50,
  },
}

const TEMP_DEFAULT = {
  format: formatTemperature,
  convert: celsiusToFahrenheit,
}

const INDOOR_TEMP_DEFAULTS = {
  ...TEMP_DEFAULT,
  icon: mdiThermometer,
  chart: {
    min: 4.5,
    max: 43,
    lower: 20,
    upper: 26,
  },
}

const LEAK_DEFAULTS = {
  icon: mdiPipeLeak,
  format: formatAlarm,
  status: statusAlarm,
}

/* eslint-disable quote-props */
export const TIMESERIES_CONFIG = {
  'air_quality/aqi': {
    icon: mdiAirFilter,
    format: formatAQI,
  },
  'air_quality/pm2_5': {
    icon: mdiAirFilter,
    format: formatParticleDensity,
  },
  'air_quality/pm10': {
    icon: mdiAirFilter,
    format: formatParticleDensity,
  },
  'air_quality/voc': {
    icon: mdiAirFilter,
    format: formatVOC,
  },
  'air_quality/co2': {
    icon: mdiAirFilter,
    format: formatPPM,
  },
  'energy/current': {
    icon: mdiPowerPlug,
    format: formatCurrent,
  },
  'energy/power': {
    icon: mdiPower,
    format: formatPower,
  },
  'energy/instantaneous_power': {
    icon: mdiPower,
    format: formatPower,
  },
  'energy/voltage': {
    icon: mdiFlash,
    format: formatVoltage,
  },
  'energy/consumption': {
    icon: mdiPower,
    format: formatPower,
  },
  'energy/generation': {
    icon: mdiSolarPower,
    format: formatPower,
  },
  'energy/net': {
    icon: mdiPower,
    format: formatPower,
  },
  'smoke_detector': {
    icon: mdiSmokeDetector,
    format: formatAlarm,
    status: statusAlarm,
  },
  'co_detector': {
    icon: mdiPeriodicTableCo2,
    format: formatAlarm,
    status: statusAlarm,
  },
  'sprinkler_flow': {
    icon: mdiShowerHead,
    format: formatAlarm,
    status: statusAlarm,
  },
  'leak/bathroom': {
    ...LEAK_DEFAULTS,
  },
  'leak/kitchen': {
    ...LEAK_DEFAULTS,
  },
  'leak/utility': {
    ...LEAK_DEFAULTS,
  },
  'leak/water_heater': {
    ...LEAK_DEFAULTS,
  },
  'leak/shower': {
    ...LEAK_DEFAULTS,
  },
  'thermostat/cooling_setpoint': {
    ...TEMP_DEFAULT,
    icon: mdiSnowflake,
  },
  'thermostat/heating_setpoint': {
    ...TEMP_DEFAULT,
    icon: mdiWhiteBalanceSunny,
  },
  'thermostat/battery': {
    icon: mdiBattery50,
    format: formatBattery,
  },
  'thermostat/mode': {
    icon: mdiThermostatBox,
    format: formatString,
  },
  'thermostat/state': {
    icon: mdiThermostat,
    format: formatString,
  },
  'temp/kitchen': {
    ...INDOOR_TEMP_DEFAULTS,
  },
  'temp/hall': {
    ...INDOOR_TEMP_DEFAULTS,
  },
  'temp/living_room': {
    ...INDOOR_TEMP_DEFAULTS,
  },
  'temp/indoor': {
    ...INDOOR_TEMP_DEFAULTS,
  },
  'temp/embedded': {
    ...TEMP_DEFAULT,
    icon: mdiThermometer,
    chart: {
      min: 4.5,
      max: 43,
    },
  },
  'temp/floor': {
    ...TEMP_DEFAULT,
    icon: mdiThermometer,
    chart: {
      min: 4.5,
      max: 43,
      lower: 20,
      upper: 26,
    },
  },
  'temp/wall': {
    ...TEMP_DEFAULT,
    icon: mdiThermometer,
    chart: {
      min: 4.5,
      max: 43,
      lower: 20,
      upper: 26,
    },
  },
  'humidity/kitchen': {
    ...INDOOR_HUMIDITY_DEFAULTS,
  },
  'humidity/hall': {
    ...INDOOR_HUMIDITY_DEFAULTS,
  },
  'humidity/living_room': {
    ...INDOOR_HUMIDITY_DEFAULTS,
  },
  'humidity/indoor': {
    ...INDOOR_HUMIDITY_DEFAULTS,
  },
  'humidity/embedded': {
    icon: mdiWaterPercent,
    format: formatHumidity,
    chart: {
      min: 0,
      max: 100,
    },
  },
  'humidity/floor': {
    icon: mdiWaterPercent,
    format: formatHumidity,
    chart: {
      min: 0,
      max: 100,
      lower: 30,
      upper: 50,
    },
  },
  'humidity/wall': {
    icon: mdiWaterPercent,
    format: formatHumidity,
    chart: {
      min: 0,
      max: 100,
      lower: 30,
      upper: 50,
    },
  },
  'hub/uptime': {
    icon: mdiChip,
    format: formatUptime,
  },
  'hub/status': {
    icon: mdiWifi,
    format: formatString,
    status: statusDeviceStatus,
  },
  'device/status': {
    icon: mdiWifi,
    format: formatString,
    status: statusDeviceStatus,
  },
  'door_access_log': {
    icon: mdiKey,
    format: formatDoorAccessLog,
    status: (v) => (v.success ? 'ok' : 'error'),
  },
  'water_usage': {
    icon: mdiCupWater,
    format: formatWaterUsage,
  },
  'door/front': {
    icon: mdiDoorOpen,
    format: formatOpenClosed,
  },
  'doorbell': {
    icon: mdiBell,
    format: formatButton,
  },
  'water/closed': {
    icon: mdiWaterOff,
    format: formatOpenClosed,
  },
  'water/open': {
    icon: mdiWater,
    format: formatClosedOpen,
  },
  'window': {
    icon: mdiWindowClosed,
    format: formatOpenClosed,
  },
}
/* eslint-enable quote-props */

const DEFAULT = {
  icon: '',
  format: formatSimple,
  convert: (v) => v,
  status: () => undefined,
}

Object.keys(TIMESERIES_CONFIG).forEach((k) => {
  // Use deepmerge so that the defaults key gets merged as well
  TIMESERIES_CONFIG[k] = deepmerge(DEFAULT, TIMESERIES_CONFIG[k])
})

export function timeseriesConfig(key) {
  return TIMESERIES_CONFIG[key] || DEFAULT
}
